/**
 * Deciding which proposals to RENDER when there are too many to draw.
 *
 * ## Why a list needs trimming at all
 *
 * The largest organization in the estate plans to **1,768 proposals** (server,
 * 2026-10-26b §5), nearly all of them single items nobody is being asked
 * about. Drawn in full that is roughly 25-30k DOM nodes of Material card, to
 * show a reader a handful of questions buried in a catalogue.
 *
 * ## Why it is a pure function in its own file
 *
 * It is small, it is stateful, and every one of its edge cases is the kind
 * that looks right and is not:
 *
 * - **A card must not vanish when it is answered.** Answering flips
 *   `needsAttention` to false, so a naive filter removes the card the reader
 *   just clicked - taking away any chance to check or undo it. Hence
 *   {@link TrimInput.sticky}, which pins anything that has ever needed an
 *   answer for the life of the view.
 * - **"Nothing needs a decision" must not render an empty page** under a
 *   heading that says there are proposals. A large all-settled list reveals a
 *   first batch instead.
 * - **The hidden count has to match what is actually withheld**, or the
 *   "show more" control lies about how much is left.
 *
 * None of those is visible to a type-check or a build, and the first two are
 * invisible on any organization small enough to escape trimming - which is
 * almost all of them. `tests/MappingListTrim.smoke.ts` pins the lot.
 *
 * No Angular import: loaded by the smoke test under plain ts-node.
 */

/**
 * Secondary ordering for the proposal list.
 *
 * ## The primary ordering is the server's and is not negotiable
 *
 * `proposals` arrives **decision-needed first** (§3.3). A near miss is
 * information, not an ask, so a group with alternatives must never displace a
 * group with a question - that would reorder the reader's actual work queue in
 * favour of something they can ignore.
 *
 * Underneath that, groups where something WAS considered and left out sort
 * above the rest. Those are where the planner's reasoning is most contestable
 * and least self-evident; a group with no alternatives is the clusterer saying
 * "nothing else came close", which needs no second look.
 *
 * ## Stability is load-bearing
 *
 * `Array.prototype.sort` is stable as of ES2019, so the server's order
 * survives within each tier. If that stopped holding, decision-needed
 * proposals would silently shuffle between loads and a reader working down the
 * list would lose their place.
 *
 * Returns a NEW array. Sorting in place would reorder the caller's list while
 * a render is reading it.
 */
export function orderProposals<T>(all: T[],
                                  needsAnswer: (item: T) => boolean,
                                  hasAlternatives: (item: T) => boolean): T[] {
  const rank = (item: T) => {
    if ( needsAnswer(item) ) {
      return 0;
    }
    return hasAlternatives(item) ? 1 : 2;
  };
  return (all || []).slice().sort((a, b) => rank(a) - rank(b));
}

export interface TrimInput<T> {
  /** Every proposal, in server order - decision-needed first. */
  all: T[];
  /** Does this one still want an answer? */
  needsAttention: (item: T) => boolean;
  /**
   * Items pinned on screen. **Mutated**: anything currently needing attention
   * is added, so it stays visible after it is answered.
   */
  sticky: Set<T>;
  /** How many settled items the reader has asked to see. */
  revealed: number;
  /** Trim only above this many items. */
  threshold: number;
  /** How many one "show more" adds. */
  batch: number;
}

export interface TrimResult<T> {
  /** Exactly what to render, in order. */
  visible: T[];
  /** Settled items deliberately withheld. */
  hidden: number;
  /** True when anything is being withheld or could be. */
  trimmed: boolean;
  /**
   * The reveal count to carry forward.
   *
   * Differs from the input only in the all-settled case, where a first batch
   * is revealed so the page is not blank. Returned rather than mutated so the
   * caller stays the single owner of that state.
   */
  revealed: number;
}

export function trimProposals<T>(input: TrimInput<T>): TrimResult<T> {
  const all = input.all || [];

  // Small enough to draw in full. No pinning, no counting, no surprises -
  // which is the case for almost every organization.
  if ( all.length <= input.threshold ) {
    return { visible: all, hidden: 0, trimmed: false, revealed: input.revealed };
  }

  for ( const item of all ) {
    if ( input.needsAttention(item) ) {
      input.sticky.add(item);
    }
  }

  const asked: T[] = [];
  const settled: T[] = [];
  for ( const item of all ) {
    if ( input.sticky.has(item) ) {
      asked.push(item);
    } else {
      settled.push(item);
    }
  }

  // Nothing to decide, but still a long list: show a first batch rather than
  // an empty page.
  let revealed = input.revealed;
  if ( asked.length === 0 && revealed === 0 ) {
    revealed = input.batch;
  }

  const extra = settled.slice(0, revealed);
  return {
    visible: asked.concat(extra),
    hidden: settled.length - extra.length,
    trimmed: true,
    revealed: revealed
  };
}
